import { Controller } from "@hotwired/stimulus"

// Global office-address lookup for onboarding.
//
// The Places result already carries the coordinates, so there is no second
// geocoding call to pay for — we read geometry.location straight off the picked
// place and post it with the form. `fields` is deliberately narrow: asking for
// anything beyond these three promotes the request to a dearer billing tier.
//
// This controller can never prevent a signup. If Places is blocked, down, or the
// key is rejected, it swaps to typed address fields and a country dropdown. A
// missing pin is a data gap we can backfill; a refused signup is gone.
export default class extends Controller {
  static targets = [
    "search", "hint", "summary", "lookup", "fallback", "notice", "countryField",
    "line1", "line2", "city", "county", "postcode", "countryCode", "latitude", "longitude"
  ]

  // The hidden fields the picked address is written into. Cleared as a set when
  // the supplier edits the search box, so a stale pick is never submitted.
  static PICKED_TARGETS = ["line1", "line2", "city", "county", "postcode", "latitude", "longitude"]

  // Google returns each part of an address as a typed component. These are the
  // types we care about, in priority order — the first match wins, because the
  // naming varies by country (UK towns come back as postal_town, not locality).
  static COMPONENT_TYPES = {
    streetNumber: ["street_number"],
    route: ["route"],
    subpremise: ["subpremise"],
    city: ["postal_town", "locality", "sublocality", "administrative_area_level_2"],
    county: ["administrative_area_level_2", "administrative_area_level_1"],
    postcode: ["postal_code"],
    country: ["country"]
  }

  static READY_TIMEOUT = 8000

  // The page renders in fallback state, so nothing here has to succeed for the
  // form to be completable — the lookup is only ever swapped in once it works.
  connect() {
    this.autocomplete = null

    if (window.google?.maps?.places) {
      this.initAutocomplete()
      return
    }

    this.onReady = () => this.initAutocomplete()
    this.onError = () => this.lookupUnavailable()
    document.addEventListener("google:maps:ready", this.onReady, { once: true })
    document.addEventListener("google:maps:error", this.onError, { once: true })

    // The script tag is async, so a blocked or very slow load never fires either
    // event. Without this timer the notice would never explain what happened.
    this.readyTimeout = setTimeout(() => this.lookupUnavailable(), this.constructor.READY_TIMEOUT)
  }

  disconnect() {
    clearTimeout(this.readyTimeout)
    document.removeEventListener("google:maps:ready", this.onReady)
    document.removeEventListener("google:maps:error", this.onError)
    if (this.placeListener) this.placeListener.remove()
  }

  initAutocomplete() {
    if (this.autocomplete || !window.google?.maps?.places) return
    clearTimeout(this.readyTimeout)

    try {
      this.autocomplete = new google.maps.places.Autocomplete(this.searchTarget, {
        fields: ["address_components", "geometry.location", "formatted_address"],
        types: ["address"]
      })
    } catch (error) {
      // A rejected key or a Places API that is not enabled throws here rather
      // than firing gm_authFailure. Leave the page in its fallback state.
      this.lookupUnavailable()
      return
    }

    this.enableLookup()

    this.placeListener = this.autocomplete.addListener("place_changed", () => this.placeSelected())

    // Picking a suggestion with Enter would otherwise submit the form before
    // place_changed has run, posting an address with no coordinates.
    this.searchTarget.addEventListener("keydown", (event) => {
      if (event.key === "Enter") event.preventDefault()
    })

    // Any retyping invalidates the previously picked address.
    this.searchTarget.addEventListener("input", () => this.clearSelection())
  }

  placeSelected() {
    const place = this.autocomplete.getPlace()

    if (!place?.geometry?.location || !place.address_components) {
      this.showError("We couldn't read that address — please pick one from the list.")
      return
    }

    const parts = this.extractComponents(place.address_components)

    this.line1Target.value = [parts.streetNumber, parts.route].filter(Boolean).join(" ")
    this.line2Target.value = parts.subpremise || ""
    this.cityTarget.value = parts.city || ""
    this.countyTarget.value = parts.county === parts.city ? "" : parts.county || ""
    this.postcodeTarget.value = parts.postcode || ""
    this.latitudeTarget.value = place.geometry.location.lat()
    this.longitudeTarget.value = place.geometry.location.lng()
    this.selectCountry(parts.countryCode)

    // A picked place with no street number is a town or region, not an office.
    if (!this.line1Target.value || !this.cityTarget.value) {
      this.clearSelection()
      this.showError("That looks like an area rather than an address — please pick your full street address.")
      return
    }

    this.showSummary(place.formatted_address)
  }

  extractComponents(components) {
    const parts = { countryCode: "" }

    for (const [key, types] of Object.entries(this.constructor.COMPONENT_TYPES)) {
      const match = types
        .map((type) => components.find((component) => component.types.includes(type)))
        .find(Boolean)

      if (!match) continue

      if (key === "country") {
        parts.countryCode = match.short_name
        parts.country = match.long_name
      } else {
        parts[key] = match.long_name
      }
    }

    return parts
  }

  // The dropdown is seeded from our own countries table. Places covers a handful
  // of territories we hold no row for, and country is the one field the server
  // will not accept as empty — so rather than submit a code that cannot resolve,
  // show the dropdown and let the supplier pick the nearest match.
  selectCountry(code) {
    const select = this.countryCodeTarget
    const known = code && Array.from(select.options).some((option) => option.value === code)

    if (!known) {
      this.countryFieldTarget.hidden = false
      return
    }

    select.value = code
    this.countryFieldTarget.hidden = true
  }

  // Guards the submit — the server enforces this too, but failing here keeps the
  // supplier on the field rather than bouncing them off a re-rendered form.
  validate(event) {
    // In fallback state the country dropdown is the only hard requirement, and
    // the browser's own `required` already covers it.
    if (!this.lookupEnabled) return

    if (this.latitudeTarget.value && this.longitudeTarget.value) return

    event.preventDefault()
    this.showError("Please pick your office address from the lookup.")
    this.searchTarget.focus()
  }

  clearSelection() {
    this.constructor.PICKED_TARGETS.forEach((target) => {
      this[`${target}Target`].value = ""
    })

    this.summaryTarget.hidden = true
    this.hintTarget.textContent = "Start typing and pick your address from the list."
    this.hintTarget.classList.remove("onboarding-page__field-hint--error")
  }

  showSummary(formattedAddress) {
    this.summaryTarget.textContent = formattedAddress
    this.summaryTarget.hidden = false
    this.hintTarget.textContent = "Address confirmed."
    this.hintTarget.classList.remove("onboarding-page__field-hint--error")
  }

  showError(message) {
    this.hintTarget.textContent = message
    this.hintTarget.classList.add("onboarding-page__field-hint--error")
  }

  // Swap the typed fields for the lookup. Only ever called once Places is known
  // to be working, so a failure anywhere before this leaves the form completable.
  enableLookup() {
    this.lookupEnabled = true

    this.lookupTarget.hidden = false
    this.fallbackTarget.hidden = true
    this.countryFieldTarget.hidden = true

    // Hidden fields still submit, so the typed inputs must not fight the picked
    // values. The country select stays enabled — it is how the country is posted.
    this.constructor.PICKED_TARGETS.forEach((target) => {
      this[`${target}Target`].value = ""
    })
  }

  // Explains why the supplier is typing rather than searching. The fields they
  // need are already on screen; this only adds the reason.
  lookupUnavailable() {
    if (this.lookupEnabled || this.autocomplete) return

    clearTimeout(this.readyTimeout)
    this.noticeTarget.hidden = false
  }
}
